import { useState, useEffect, useRef } from 'react';
import { useToast } from '../../hooks/useToast';
import { useConfirm } from '../../hooks/useConfirm';
import { useBusy, useBusyKeys } from '../../hooks/useBusy';
import { useAfterWrite } from '../../hooks/useAfterWrite';
import { logBackgroundError } from '../../observability';
import { useI18n, formatDay, formatDate } from '../../i18n';
import { useLabels, countLabel } from '../../i18n/labels';
import { useDeferredLoading } from '../../hooks/useDeferredLoading';
import { useOnResume } from '../../lib/resume';
import { useAuthStore } from '../../store/auth';
import { CheckIcon, XMarkIcon, WarningIcon } from '../shared/Icons';
import PushOffHint from '../PushOffHint';
import { personName, nameMatches, textMatches, compareNames } from '../../utils/personName';
import { todayLocal } from '../../utils/calendarDate';
import { toKg } from '../../utils/weight';
import {
  getWeighins, markWeighin, endWeighin, proposeEndWeighin, cancelEndWeighin,
} from '../../api/client';
import type { Tournament, WeighinAthlete, WeighinProposal, WeighinProposalsByDay } from '../../types';
import { BADGE_XS, BADGE_XXS } from '../shared/badgeSize';

interface WeighinTabProps {
  id: string;
  tournament: Tournament;
  tournamentDays: string[];
  weighins: WeighinAthlete[];
  setWeighins: (fn: (prev: WeighinAthlete[]) => WeighinAthlete[]) => void;
  /** #556 — the ended days, by date. */
  weighinEndedDays: Record<string, boolean>;
  setWeighinEnded: (date: string, ended: boolean) => void;
  /** #548 — the open End proposal of each day: GET /:id/weighins and the
   *  socket events. The tab reads the proposal of its day. */
  weighinProposal: WeighinProposalsByDay;
  setWeighinProposal: (day: string, v: WeighinProposal | null) => void;
  reload: () => Promise<void>;
}

/** The kg over the limit, to 0.01 kg: the rounding of the server
 *  (lib/weightClassLimit.ts). 71.4 - 71 is 0.4000000000000057 in a float. */
function overKg(weight: number, limit: number): number {
  return Math.round((weight - limit) * 100) / 100;
}

/** The weigh-in of a championship, for each day (#548, #549). An open ring
 *  and a hybrid use the Check-in tab. Both call `PATCH /:id/weighins/:regId`.
 *
 *  The championship rule (operator, 2026-10-03): the athlete stays in the
 *  registered class, or the bout is a Walk Over. So "Ready" needs the scale
 *  weight. Over the maximum of the class (IFMA §11.3.1) the tab asks first:
 *  Confirm marks the athlete ready (the manager is trusted), Cancel keeps the
 *  athlete not ready, and End Weigh-in gives the Walk Over.
 *
 *  End Weigh-in needs two people (the 4-eye rule) only when the tournament
 *  lists two or more managers (#557, operator 2026-10-04): one person
 *  proposes, and another manager of the tournament or an admin confirms. The
 *  proposal is a row on the server, so the banner shows after a reload too.
 *  With one listed manager, the propose answers `direct`, and the End runs at
 *  once after its dialog. */
export default function WeighinTab({
  id, tournament, tournamentDays,
  weighins, setWeighins, weighinEndedDays, setWeighinEnded,
  weighinProposal, setWeighinProposal, reload,
}: WeighinTabProps) {
  const { showError, showSuccess, showWarning } = useToast();
  const { confirm } = useConfirm();
  // #451, #548 review — the warnings of the End (a bout whose two athletes
  // are absent). One function for End Weigh-in and End Check-in (#557 review):
  // the End shows its warnings and no success text, because the socket event
  // `weighin:ended` shows the summary in the language of the user (#548 review).
  const afterWrite = useAfterWrite();
  const { t, lang } = useI18n();
  const wt = t.weighin;
  // #531 — "3 pasiruošę", "10 pasiruošusių": the count takes the plural form.
  const { readyCount: readyLabel } = useLabels();
  const notReadyLabel = (n: number) => countLabel(lang, n, { one: wt.notReadyOne, few: wt.notReadyFew, other: wt.notReadyOther });
  const kgText = (n: number) => n.toLocaleString(lang === 'lt' ? 'lt-LT' : 'en-GB', { maximumFractionDigits: 2 });
  const { user } = useAuthStore();

  const [weighinDate, setWeighinDate] = useState('');
  const [weighinLoading, setWeighinLoading] = useState(false);
  // #458 — the loading text shows after 200 ms and holds 400 ms (useDeferredLoading):
  // a fast load shows nothing, not a one-frame flash.
  const showWeighinLoading = useDeferredLoading(weighinLoading);
  const [weighinSearch, setWeighinSearch] = useState('');
  const [weighinSaved, setWeighinSaved] = useState<string | null>(null);
  /** #549 — the typed scale weight of each row, until the row is saved. The
   *  60-second refresh and the socket events do not overwrite it. */
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  // #605, #664 — one write at a time for each row: a tap on another row stays live
  // while the first row saves (a global lock dropped that tap with no message).
  const rowSaves = useBusyKeys();
  // The three End actions share one lock.
  const ending = useBusy();

  /** The End seen last. A load writes it first, so only an End from the
   *  socket (`weighin:ended`) reads the list again (the effect below). */
  // #556 — the flag of the selected day only.
  const weighinEnded = !!weighinDate && !!weighinEndedDays[weighinDate];
  const endedRef = useRef(weighinEnded);

  /** One answer of GET /:id/weighins for day `d` into the state of the page. */
  function land(d: string, data: { athletes: WeighinAthlete[]; weigh_in_ended: boolean; proposal: WeighinProposal | null }) {
    setWeighins(() => data.athletes ?? []);
    endedRef.current = data.weigh_in_ended || false;
    setWeighinEnded(d, data.weigh_in_ended || false);
    // #548 — the open proposal of the day: a page that opens after the
    // proposal shows the banner. Only the socket event set it before.
    setWeighinProposal(d, data.proposal ?? null);
  }

  // #665 review — false after the unmount. A change of the tab keeps the
  // path (?tab=), so the page binding of useConfirm() does not see it: a
  // question after an await reads this ref first.
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  // Load the list of one day. Only the latest request writes the list: a slow
  // answer for an earlier day does not replace the day that the user chose.
  const daySeq = useRef(0);
  async function selectDay(d: string) {
    const seq = ++daySeq.current;
    setWeighinDate(d);
    setDrafts({});
    if (!d) { setWeighins(() => []); setWeighinLoading(false); return; }
    setWeighinLoading(true);
    try {
      const res = await getWeighins(id, d);
      if (seq !== daySeq.current) return;
      land(d, res.data);
    } catch (err) {
      if (seq !== daySeq.current) return;
      // #559 — the empty list said "no athletes" after a failed load, and a
      // manager could read it as "nobody registered". The last list stays,
      // and the error shows.
      setWeighinEnded(d, false); setWeighinProposal(d, null);
      showError(err);
    } finally {
      if (seq === daySeq.current) setWeighinLoading(false);
    }
  }

  /** Read the selected day again, behind the page. A newer selectDay() wins. */
  async function refreshDay() {
    const d = weighinDate;
    if (!d) return;
    const seq = daySeq.current;
    const res = await getWeighins(id, d);
    if (seq !== daySeq.current) return;
    land(d, res.data);
  }

  // #552 — the tab opened as an empty card. Select today when it is a day of
  // the tournament, else the first day, and load that list.
  useEffect(() => {
    if (!tournament.is_locked || tournamentDays.length === 0) return;
    const today = todayLocal();
    void selectDay(tournamentDays.includes(today) ? today : tournamentDays[0]);
    // Once, on mount: a later change of the day is the user's choice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh the list every 60 s while a day is selected and its weigh-in is
  // not ended. It also brings a proposal that a lost socket event missed.
  useEffect(() => {
    if (!weighinDate || !id || weighinEnded) return;
    const timer = setInterval(() => { refreshDay().catch(logBackgroundError('WeighinTab:refreshDay')); }, 60_000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weighinDate, id, weighinEnded]);

  // #499 — after a gap in the socket, read the day again: the events of the
  // gap (a mark, a proposal, the End) are lost.
  useOnResume(() => refreshDay(), 'weighin');

  // The End of another manager arrives as `weighin:ended`: read the list
  // again, so each absent athlete shows the WO badge.
  useEffect(() => {
    if (weighinEnded && !endedRef.current) refreshDay().catch(logBackgroundError('WeighinTab:refreshDay'));
    endedRef.current = weighinEnded;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weighinEnded]);

  /** Ready ⇄ not ready. #549 — Ready needs the scale weight, and over the
   *  limit of the class the manager confirms first. */
  async function toggleReady(w: WeighinAthlete) {
    if (weighinEnded) return;
    const regId = w.registration_id;
    const flash = () => { setWeighinSaved(regId); setTimeout(() => setWeighinSaved(null), 1200); };
    if (w.weigh_in_status === 'ready') {
      await rowSaves.run(regId, async () => {
        try {
          // No `weight_kg` key: the weight of the day stays (#455).
          await markWeighin(id, regId, { date: weighinDate, status: 'pending' });
          setWeighins(prev => prev.map(x => x.registration_id === regId ? { ...x, weigh_in_status: 'pending' } : x));
          flash();
        } catch (err) { showError(err); }
      });
      return;
    }
    const kg = toKg(drafts[regId] ?? w.weigh_in_weight);
    if (kg == null) { showWarning(wt.weightFirst); return; }
    const limit = w.class_max_kg ?? null;
    let asked = false;
    if (limit != null && kg > limit) {
      // IFMA §11.3.1 — no lower limit, a maximum. Cancel writes nothing: the
      // athlete stays not ready, and End Weigh-in gives the Walk Over. The
      // confirm button keeps the label "Confirm": the text of the question and
      // the Manual name it (#665 review).
      const text = wt.overLimitConfirm
        .replace('{weight}', kgText(kg)).replace('{over}', kgText(overKg(kg, limit))).replace('{limit}', kgText(limit));
      if (!(await confirm({ message: text, focusCancel: true }))) return;
      asked = true;
    }
    await rowSaves.run(regId, async () => {
      try {
        const res = await markWeighin(id, regId, { date: weighinDate, status: 'ready', weight_kg: kg });
        setWeighins(prev => prev.map(x => x.registration_id === regId
          ? { ...x, weigh_in_status: 'ready', weigh_in_weight: res.data.weight_kg ?? kg } : x));
        setDrafts(prev => { const next = { ...prev }; delete next[regId]; return next; });
        // The server is the judge of the limit. When the tab did not ask (no
        // limit known here), its warning shows.
        if (!asked) await afterWrite({ source: 'WeighinTab:ready', warnings: res.data.warnings });
        flash();
      } catch (err) { showError(err); }
    });
  }

  const readyCount = weighins.filter(w => w.weigh_in_status === 'ready').length;
  const notReadyCount = weighins.length - readyCount;

  /** End Weigh-in at a championship: with two or more listed managers, the
   *  first person proposes (#548); with one, the End runs directly (#557). */
  async function proposeEnd() {
    if (ending.busy) return;
    // The answer of the server tells if the End runs directly. The question
    // comes after the first run() and before the second, so the busy lock is
    // free while the person reads (#665).
    let direct = false;
    await ending.run(async () => {
      try {
        const res = await proposeEndWeighin(id, weighinDate);
        if (res.data.direct) { direct = true; return; }
        setWeighinProposal(res.data.date ?? weighinDate, res.data);
        showSuccess(wt.proposed);
      } catch (err) {
        showError(err);
        // 409 WEIGHIN_PROPOSAL_EXISTS: the proposal of another person is open.
        refreshDay().catch(logBackgroundError('WeighinTab:refreshDay'));
      }
    });
    // The person left the tab while the server answered: no question.
    if (!direct || !mounted.current) return;
    // #557 — the tournament lists one manager: no second person. The
    // question names the counts, and its confirm button ends the weigh-in at once.
    if (!(await confirm({
      message: t.tournament.endWeighinConfirm.replace('{ready}', String(readyCount)).replace('{notReady}', String(notReadyCount)),
      confirmLabel: t.tournament.endWeighIn,
      destructive: true,
    }))) return;
    await ending.run(async () => {
      try {
        const end = await endWeighin(id, weighinDate);
        setWeighinProposal(weighinDate, null); setWeighinEnded(weighinDate, true);
        await afterWrite({ source: 'WeighinTab:end-direct', warnings: end.data.warnings, refresh: reload });
      } catch (err) {
        showError(err);
        refreshDay().catch(logBackgroundError('WeighinTab:refreshDay'));
      }
    });
  }

  /** The second person confirms: the cascade of Walk Overs runs. */
  async function confirmEnd() {
    if (ending.busy) return;
    if (!(await confirm({ message: wt.confirmPrompt, confirmLabel: t.tournament.endWeighIn, destructive: true }))) return;
    await ending.run(async () => {
      try {
        const res = await endWeighin(id, weighinDate);
        setWeighinProposal(weighinDate, null); setWeighinEnded(weighinDate, true);
        await afterWrite({ source: 'WeighinTab:end-confirm', warnings: res.data.warnings, refresh: reload });
      } catch (err) {
        showError(err);
        refreshDay().catch(logBackgroundError('WeighinTab:refreshDay'));
      }
    });
  }

  /** The proposer, another manager or an admin cancels the proposal. */
  async function cancelProposal() {
    await ending.run(async () => {
      try {
        await cancelEndWeighin(id, weighinDate);
        setWeighinProposal(weighinDate, null);
      } catch (err) { showError(err); }
    });
  }

  if (!tournament.is_locked || tournamentDays.length === 0) {
    return (
      <div className="card p-6 text-sm text-gray-500">
        {t.tournament.weighIn} — {wt.lockedFirst}
      </div>
    );
  }

  // A day has its own proposal: the tab reads the proposal of its day.
  const proposal = (weighinDate && weighinProposal[weighinDate]) || null;
  // #548 review — read the fields with a guard: during a release, an older
  // backend can send a proposal without `confirmers` or `proposedAt`, and a
  // throw here takes the whole tournament page down.
  const confirmerNames = proposal
    ? (proposal.confirmers ?? []).map(c => personName(c.firstName, c.lastName)).filter(Boolean).join(', ')
    : '';
  const proposedTime = proposal?.proposedAt && !Number.isNaN(Date.parse(proposal.proposedAt))
    ? formatDate(proposal.proposedAt, 'HH:mm')
    : '—';

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="font-semibold text-sm">{t.tournament.weighIn}</div>
        <select
          className="input w-auto text-xs"
          data-testid="weighin-day"
          value={weighinDate}
          onChange={e => { void selectDay(e.target.value); }}>
          <option value="">{t.tournament.selectDay}</option>
          {tournamentDays.map((d: string) => (
            <option key={d} value={d}>{formatDay(d, 'EEE dd MMM')}</option>
          ))}
        </select>
      </div>

      {!weighinDate && <p className="text-xs text-gray-400">{t.tournament.selectDayHint}</p>}
      {weighinDate && showWeighinLoading && <p className="text-xs text-gray-400">{t.common.loading}</p>}

      {weighinDate && !weighinLoading && weighins.length > 0 && (() => {
        const sorted = [...weighins].sort(compareNames);
        const wSearch = weighinSearch.trim();
        const filteredWeighins = wSearch
          ? sorted.filter(w => nameMatches(wSearch, w.first_name, w.last_name) || textMatches(wSearch, w.team_name))
          : sorted;
        return (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              <input type="text" placeholder={wt.filterPlaceholder} value={weighinSearch}
                onChange={e => setWeighinSearch(e.target.value)} className="input w-48 text-xs py-1" />
              <div className="text-xs text-gray-400" data-testid="weighin-counts">
                {readyLabel(readyCount)} · {notReadyLabel(notReadyCount)}
              </div>
            </div>
            <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
              {filteredWeighins.map(w => {
                const regId = w.registration_id;
                const ready = w.weigh_in_status === 'ready';
                const absent = w.weigh_in_status === 'absent';
                const savedKg = toKg(w.weigh_in_weight);
                const shown = drafts[regId] ?? (savedKg == null ? '' : String(savedKg));
                const typedKg = toKg(shown);
                const limit = w.class_max_kg ?? null;
                const over = savedKg != null && limit != null && savedKg > limit ? overKg(savedKg, limit) : null;
                const saving = rowSaves.isBusy(regId);
                // #549 — Ready needs a weight; not ready needs nothing.
                const canToggle = !weighinEnded && !saving && (ready || typedKg != null);
                return (
                  <div key={regId} data-testid="weighin-row" data-registration-id={regId}
                    data-status={w.weigh_in_status} data-class-max={limit ?? ''}
                    className={`py-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm transition-colors duration-500 ${weighinSaved === regId ? 'bg-green-100' : ''}`}>
                    <button type="button" data-testid="weighin-ready" disabled={!canToggle}
                      onClick={() => { void toggleReady(w); }}
                      title={ready ? wt.markNotReady : typedKg == null ? wt.weightFirst : wt.markReady}
                      aria-label={ready ? wt.markNotReady : wt.markReady}
                      aria-pressed={ready}
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                        weighinEnded ? 'opacity-40 cursor-not-allowed' : !canToggle ? 'opacity-50 cursor-not-allowed' : ''} ${
                        ready ? 'bg-green-500 border-green-500 text-white' :
                        absent ? 'bg-red-500 border-red-500 text-white' : 'bg-white border-gray-300'
                      }`}>
                      {ready && <CheckIcon className="w-3 h-3" />}
                      {absent && <XMarkIcon className="w-3 h-3" />}
                    </button>
                    <div className="flex-1 min-w-[8rem]">
                      <span className={`font-medium ${absent ? 'text-red-500 line-through' : ''}`}>{personName(w.first_name, w.last_name)}</span>
                      <span className="text-xs text-gray-400 ml-1.5">{w.team_name}</span>
                      {absent && <span className={`badge ${BADGE_XXS} px-1 bg-red-100 text-red-700 border-red-200 ml-1`}>WO</span>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <input type="number" inputMode="decimal" step="0.1" min="0"
                        data-testid="weighin-weight"
                        aria-label={wt.weightLabel} title={wt.weightLabel}
                        placeholder={wt.weightPlaceholder}
                        value={shown}
                        disabled={weighinEnded || ready || saving}
                        onChange={e => { const v = e.target.value; setDrafts(prev => ({ ...prev, [regId]: v })); }}
                        onKeyDown={e => { if (e.key === 'Enter' && !ready && typedKg != null) void toggleReady(w); }}
                        className="input w-20 text-xs py-1 text-right disabled:bg-gray-50" />
                      {over != null && (
                        <span data-testid="weighin-over-limit" title={wt.overBadgeTitle.replace('{limit}', kgText(limit!))}
                          className={`badge ${BADGE_XS} bg-amber-100 text-amber-800 border-amber-200`}>
                          {wt.overBadge.replace('{over}', kgText(over))}
                        </span>
                      )}
                      <span className={`badge badge-gray ${BADGE_XS}`}>{w.age_group}</span>
                      <span className={`badge badge-gray ${BADGE_XS}`}>{w.weight_class}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* #557 — the End proposal and the End summary push. A line shows when this device has no push subscription. */}
            {!weighinEnded && <PushOffHint />}
            {weighinEnded ? (
              <div data-testid="weighin-completed" className="badge badge-green text-xs inline-flex items-center gap-1"><CheckIcon className="w-3 h-3" /> {t.tournament.weighInCompleted}</div>
            ) : proposal ? (
              // #548 — the banner names the proposer and the people who can
              // confirm, so nobody waits for a manager row that nobody uses.
              <div data-testid="weighin-proposal" className="card p-3 bg-amber-50 border border-amber-200 space-y-2">
                <div data-testid="weighin-proposal-proposer" className="text-sm font-semibold text-amber-800 flex items-start gap-1.5">
                  <WarningIcon className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{wt.proposedBy
                    .replace('{name}', personName(proposal.proposerFirstName, proposal.proposerLastName))
                    .replace('{time}', proposedTime)}</span>
                </div>
                <div className="text-xs text-amber-700">
                  {wt.proposalCounts.replace('{ready}', readyLabel(readyCount)).replace('{notReady}', notReadyLabel(notReadyCount))}
                </div>
                <div data-testid="weighin-proposal-confirmers" className="text-xs text-amber-700">
                  {confirmerNames ? wt.confirmers.replace('{names}', confirmerNames) : wt.confirmersAdminOnly}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {proposal.proposedBy !== user?.id ? (
                    <button type="button" data-testid="weighin-confirm-end" disabled={ending.busy}
                      onClick={() => { void confirmEnd(); }} className="btn-primary btn-sm">
                      {t.tournament.confirmEndWeighin}
                    </button>
                  ) : (
                    <span data-testid="weighin-waiting" className="text-xs text-amber-600 italic">{wt.waiting}</span>
                  )}
                  <button type="button" data-testid="weighin-cancel-end" disabled={ending.busy}
                    onClick={() => { void cancelProposal(); }} className="btn-outline btn-sm">
                    {wt.cancelProposal}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                <button type="button" data-testid="weighin-end" onClick={() => { void proposeEnd(); }}
                  disabled={readyCount === 0 || ending.busy} className="btn-outline-red btn-sm">
                  {t.tournament.endWeighIn} ({readyLabel(readyCount)}, {notReadyLabel(notReadyCount)})
                </button>
                <p className="text-xs text-gray-400">{wt.endHint}</p>
              </div>
            )}
          </>
        );
      })()}

      {weighinDate && !weighinLoading && weighins.length === 0 && (
        <p className="text-xs text-gray-400">{wt.noAthletes}</p>
      )}
    </div>
  );
}
